// Authentication logic for Webapp and Salesforce users.

import * as QS from 'querystring';
import assert  from './assert';
import ms      from 'ms';
import retry   from 'async-retry';
import rollbar from './rollbar';
import xhr     from 'xhr';


/* eslint-disable camelcase */


// Used for authenticating users with email/passwords, credentials are public so
// do not use for any other purpose.
const appClientID     = '6iyJwXyIQnTisCrXEXBBm0sjvex4u37y';
const appClientSecret = 'GwERZ9SeQ-gT-4JCi8vTBn8tAUoCqM-NichDYaskNLkFukVCSZ9MNckadu-QhQzM';

// Used for authenticating with Salesforce.
const adminClientID = 'ZnzG3XvNRNABhCqBy3mC24cYR02SSlG5';


export async function authenticate({ username, password }) {
  const response = await getOAuthToken({
    grant_type: 'http://auth0.com/oauth/grant-type/password-realm',
    audience:   'https://webapp.broadly.com',
    realm:      'webapp',
    scope:      'openid profile email offline_access',
    username,
    password
  });

  const jwtToken = response.access_token;
  assert(jwtToken, 'Auth0 returns 200 and no access_token');
  const refreshToken = response.refresh_token; // eslint-disable-line no-shadow
  assert(refreshToken, 'Auth0 returns 200 and no refresh_token');
  const expiresIn = Date.now() + ms('15m');

  return { jwtToken, refreshToken, expiresIn };
}


export function authenticateWithOAuth(connection, userID) {
  const returnURL   = window.location.href;
  const state       = QS.stringify({ connection, returnURL, userID });
  const query       = {
    client_id:     adminClientID,
    connection,
    redirect_uri:  window.location.origin,
    state,
    response_type: 'token',
    audience:      'https://webapp.broadly.com',
    scope:         'openid api profile email offline_access'
  };
  const queryString = QS.stringify(query);
  const url         = `https://broadly.auth0.com/authorize?${queryString}`;
  window.location   = url;
}


export function isOAuthCallback() {
  const authResult     = parseHashAsQueryString();
  const hasAccessToken = !!authResult.access_token;
  return hasAccessToken;
}


export function needsSalseforceAuthentication() {
  const referer        = document.referrer && new URL(document.referrer);
  const usesSalesforce = referer && (isSalesforceReferer(referer) || isBroadlyAdminReferer(referer));
  return usesSalesforce && !isOAuthCallback();
}


export function getOAuthResult() {
  const authResult = parseHashAsQueryString();
  const jwtToken   = authResult.access_token;

  if (jwtToken) {
    const state       = QS.parse(authResult.state);
    const accessToken = {
      jwtToken,
      expiresIn:  Date.now() + ms('1h'),
      connection: state.connection
    };

    const returnURL = state.returnURL;

    return {
      accessToken,
      returnURL
    };
  } else
    return {};
}


export async function refreshToken(accessToken) {
  try {
    const response = await getOAuthToken({
      grant_type:    'refresh_token',
      refresh_token: accessToken.refreshToken
    });

    const jwtToken = response.access_token;
    assert(jwtToken, 'Auth0 returns 200 and no access_token');
    return {
      jwtToken,
      refreshToken: accessToken.refreshToken,
      expiresIn:    Date.now() + ms('1h')
    };
  } catch (error) {
    const isRevoked = (error.statusCode === 401);
    if (isRevoked)
      return { revoked: true };
    else
      throw error;
  }
}


function isSalesforceReferer(referer) {
  const { hostname } = referer;

  if (hostname) {
    const isVisualForce       = hostname.endsWith('.visual.force.com');
    const isBroadlySalesforce = hostname === 'broadly.my.salesforce.com';

    return isVisualForce || isBroadlySalesforce;
  } else
    return false;
}


function isBroadlyAdminReferer(referer) {
  const { hostname }   = referer;
  const isAdminReferer = hostname === 'admin.broadly.com' || hostname === 'admin.broadly.localhost';
  return isAdminReferer;
}


function parseHashAsQueryString() {
  return QS.parse(window.location.hash?.substring(1));
}

const defaultRetryOptions = {};
const testRetryOptions    = { minTimeout: 0, retries: 1 };
const isTest              = (process.env.NODE_ENV === 'test');
const retryOptions        = isTest ? testRetryOptions : defaultRetryOptions;

async function getOAuthToken(params) {
  const response = await retry(async function() {
    return await new Promise(function(resolve, reject) {
      xhr({
        url:    'https://broadly.auth0.com/oauth/token',
        method: 'POST',
        body:   {
          client_id:     appClientID,
          client_secret: appClientSecret,
          ...params
        },
        json:    true,
        timeout: ms('5s')
      }, function(error, res) {
        if (error) {
          const authError      = new Error(`Authentication Error: ${error.message || error.code || error}`);
          authError.statusCode = error.statusCode;
          authError.name       = 'AuthenticationError';
          reject(authError);
        } else
          resolve(res);
      });
    });
  }, retryOptions);

  const { body } = response;
  if (response.statusCode === 200)
    return body;
  else if (body && body.error === 'invalid_grant') {
    // OAuth spec says if refresh token is invalid, expired or revoked,
    // then the status code would be 400, and the error code would be
    // 'invalid_grant': https://tools.ietf.org/html/rfc6749#section-5.2
    //
    // Auth0 responds with status code 403, but the correct error code.
    const accessError      = new Error(body.error);
    accessError.statusCode = 401;
    throw accessError;
  } else {
    const message          = body ?
      (body.message || JSON.stringify(body)) :
      response.statusCode;
    const statusError      = new Error(message);
    statusError.statusCode = response.statusCode;
    throw statusError;
  }
}

export function forgotPassword(email) {
  return new Promise(function(resolve, reject) {
    xhr({
      url:    'https://broadly.auth0.com/dbconnections/change_password',
      method: 'POST',
      body:   {
        client_id:  appClientID,
        connection: 'webapp',
        email
      },
      json: true
    }, function(error, response, body) {
      if (error) {
        rollbar.error(error);
        reject(error);
      } else if (response.statusCode === 200)
        resolve(body);
      else
        reject();
    });
  });
}


export function isPasswordlessAuthentication() {
  const { hash }           =  window.location;
  const isPasswordlessPath =  hash.startsWith('#/signin/passwordless');

  if (!isPasswordlessPath)
    return false;

  const { username, password } = getPasswordlessCredentials();
  const hasNeededQueryString   = !!username && !!password;

  return hasNeededQueryString;
}


export function getPasswordlessCredentials() {
  const { hash }            = window.location;
  const query               = hash.substr(hash.indexOf('?') + 1);
  const { username, token } = QS.parse(query);

  return {
    username,
    password: token
  };
}


export function passwordless(phone) {
  return new Promise(function(resolve, reject) {
    xhr({
      url:    `${API_URL}/passwordless`,
      method: 'POST',
      body:   {
        phone
      },
      json: true
    }, function(error, response) {
      if (error) {
        rollbar.error(error);
        reject(error);
      } else if (response.statusCode === 200)
        resolve();
      else
        reject();
    });
  });
}
